import { Grid, Typography } from '@mui/material';
import { memo } from 'react';

import {
  convertApartmentDestination,
  convertBuildingImplantation,
  convertBuildingStandard,
  convertEstimatedAge,
  convertGroundFloorOccupation,
  convertPreservationState,
  convertRoofTopCharacterization,
  convertWetAreaFloorFinish,
  convertWetAreaWallFinish,
} from '../../../api/characteristics/enumerations';
import {
  ApartmentBuilding as ApartmentBuildingType,
  SectionStatus,
} from '../../../api/characteristics/types';
import { SectionStatusBadge } from '../components/StatusIndicators';
import { AnswerTag, AnswerTagsBox, StyledDivider } from '../styles';

interface ApartmentBuildingSectionProps {
  formData: ApartmentBuildingType;
  sectionStatus?: SectionStatus;
}

export const ApartmentBuildingSection = memo(function ApartmentBuildingSection({
  formData,
  sectionStatus,
}: ApartmentBuildingSectionProps) {
  return (
    <Grid container spacing={2} marginBottom={3}>
      <Grid item xs={12}>
        <SectionStatusBadge status={sectionStatus} />
      </Grid>
      <Grid item xs={12}>
        <Typography fontSize={18} fontWeight={600} id="building-dados-predio">
          Dados do Prédio
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Implantação
            </Typography>
            <Typography>
              {formData.general_info.implantation
                ? convertBuildingImplantation(
                    formData.general_info.implantation
                  )
                : '-'}
            </Typography>
          </Grid>
          <Grid item xs={9} />
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Número de Pavimentos
            </Typography>
            <Typography>
              {formData.general_info.number_of_floors ?? '-'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Número de Unidades
            </Typography>
            <Typography>
              {formData.general_info.number_of_units ?? '-'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Número de Subsolos
            </Typography>
            <Typography>
              {formData.general_info.number_of_basements ?? '-'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Número de Elevadores
            </Typography>
            <Typography>
              {formData.general_info.number_of_elevators ?? '-'}
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <StyledDivider />
      </Grid>
      <Grid item xs={12}>
        <Typography
          fontSize={18}
          fontWeight={600}
          id="building-caracterizacao-cobertura"
        >
          Caracterização da Cobertura
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <AnswerTagsBox>
          {(formData.construction_system.roof_characterization ?? []).length >
          0 ? (
            (formData.construction_system.roof_characterization ?? []).map(
              (v) => (
                <AnswerTag key={v}>
                  {convertRoofTopCharacterization(v)}
                </AnswerTag>
              )
            )
          ) : (
            <Typography>-</Typography>
          )}
          {formData.construction_system.roof_characterization_other && (
            <AnswerTag>
              {formData.construction_system.roof_characterization_other}
            </AnswerTag>
          )}
        </AnswerTagsBox>
      </Grid>

      <Grid item xs={12}>
        <StyledDivider />
      </Grid>
      <Grid item xs={12}>
        <Typography fontSize={18} fontWeight={600} id="building-conservacao">
          Conservação
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Idade Estimada
            </Typography>
            <Typography>
              {formData.conservation.estimated_age
                ? convertEstimatedAge(formData.conservation.estimated_age)
                : '-'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Estado de Conservação
            </Typography>
            <Typography>
              {formData.conservation.preservation_state
                ? convertPreservationState(
                    formData.conservation.preservation_state
                  )
                : '-'}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Destinação
            </Typography>
            <Typography>
              {formData.conservation.destination
                ? convertApartmentDestination(formData.conservation.destination)
                : '-'}
            </Typography>
          </Grid>
          {formData.conservation.destination_other && (
            <Grid item xs={3}>
              <Typography variant="caption" color="text.secondary">
                Destinação - Outros
              </Typography>
              <Typography>{formData.conservation.destination_other}</Typography>
            </Grid>
          )}
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <StyledDivider />
      </Grid>
      <Grid item xs={12}>
        <Typography
          fontSize={18}
          fontWeight={600}
          id="building-ocupacao-terreo"
        >
          Ocupação de Térreo
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <AnswerTagsBox>
          {(formData.construction_system.ground_floor_occupation ?? []).length >
          0 ? (
            (formData.construction_system.ground_floor_occupation ?? []).map(
              (v) => (
                <AnswerTag key={v}>{convertGroundFloorOccupation(v)}</AnswerTag>
              )
            )
          ) : (
            <Typography>-</Typography>
          )}
          {formData.construction_system.ground_floor_occupation_other && (
            <AnswerTag>
              {formData.construction_system.ground_floor_occupation_other}
            </AnswerTag>
          )}
        </AnswerTagsBox>
      </Grid>

      <Grid item xs={12}>
        <StyledDivider />
      </Grid>
      <Grid item xs={12}>
        <Typography
          fontSize={18}
          fontWeight={600}
          id="building-caracteristicas-gerais"
        >
          Características Gerais
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography variant="caption" color="text.secondary">
              Padrão de Acabamento
            </Typography>
            <Typography>
              {formData.conservation.finishing_standard
                ? convertBuildingStandard(
                    formData.conservation.finishing_standard
                  )
                : '-'}
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Typography fontWeight={600}>Pisos</Typography>
      </Grid>
      <Grid item xs={12}>
        <AnswerTagsBox>
          {(formData.conservation.finishing_areas?.floors ?? []).length > 0 ? (
            (formData.conservation.finishing_areas?.floors ?? []).map((v) => (
              <AnswerTag key={v}>{convertWetAreaFloorFinish(v)}</AnswerTag>
            ))
          ) : (
            <Typography>-</Typography>
          )}
          {formData.conservation.finishing_areas?.floors_other && (
            <AnswerTag>
              {formData.conservation.finishing_areas.floors_other}
            </AnswerTag>
          )}
        </AnswerTagsBox>
      </Grid>
      <Grid item xs={12} mt={2}>
        <Typography fontWeight={600}>Paredes</Typography>
      </Grid>
      <Grid item xs={12}>
        <AnswerTagsBox>
          {(formData.conservation.finishing_areas?.walls ?? []).length > 0 ? (
            (formData.conservation.finishing_areas?.walls ?? []).map((v) => (
              <AnswerTag key={v}>{convertWetAreaWallFinish(v)}</AnswerTag>
            ))
          ) : (
            <Typography>-</Typography>
          )}
          {formData.conservation.finishing_areas?.walls_other && (
            <AnswerTag>
              {formData.conservation.finishing_areas.walls_other}
            </AnswerTag>
          )}
        </AnswerTagsBox>
      </Grid>
    </Grid>
  );
});
